import { CarPart } from "types/CarPart";

import { Operation, OperationColors } from "./types";

type CarProps = {
  damagedParts: Partial<Record<CarPart, {
    operation: Operation;
    replacementPartCost?: string | null;
  }>>;
  totalLoss?: boolean;
  orientation?: "vertical" | "horizontal";
  scale?: number;
};

export const Car = ({ damagedParts, orientation, totalLoss, scale }: CarProps): JSX.Element => {
  const getFill = (partId: CarPart) => {
    const damagedPart = damagedParts[partId];

    if (!damagedPart) {
      return OperationColors.NONE.partColor;
    }

    const { operation} = damagedPart;

    if (!operation) {
      return OperationColors.NONE.partColor;
    }

    if (totalLoss) {
      return OperationColors.REPLACE.partColor;
    }

    return OperationColors[operation].partColor || OperationColors.DEFAULT.partColor;
  };

  const height = orientation === "vertical" ? "211" : "106";

  const transformRotate = orientation === "vertical" ? "rotate(-90)" : undefined;
  const transformScale = scale !== undefined ? `scale(${scale})` : undefined;
  const transform = [transformRotate, transformScale].join(" ");

  return (
    <svg
      transform={transform}
      width="211"
      height={height}
      viewBox="0 0 211 106"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_10921_24384)" filter="url(#filter0_d_10921_24384)">
        <path
          d="M192.187 67.3833C188.77 70.723 180.333 79.6341 173.043 86.759C177.122 87.2105 182.164 84.7851 185.581 80.7773C189.66 75.9935 192.187 67.3833 192.187 67.3833Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("RHEADLAMP")}
        />
        <path
          d="M191.584 33.6734C188.167 30.3337 179.729 21.4226 172.439 14.2977C176.519 13.8462 181.833 16.0637 184.978 20.2793C191.584 29.1354 191.584 33.6734 191.584 33.6734Z"
          fill={getFill("LHEADLAMP")}
          stroke="#12191F"
          strokeWidth="1.43421"
        />
        <path d="M131.986 85.464L128.675 91.9386L90.2693 91.2911L92.2558 84.8166L92.2558 77.6946C116.756 78.342 131.986 85.464 131.986 85.464Z" />
        <path
          d="M92.2558 84.8166L131.986 85.464M92.2558 84.8166L90.2693 91.2911L128.675 91.9386L131.986 85.464M92.2558 84.8166L92.2558 77.6946C116.756 78.342 131.986 85.464 131.986 85.464"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("FRDOOR")}
        />
        <path d="M131.986 15.5389L128.675 9.06433L90.2693 9.71179L92.2558 16.1863L92.2558 23.3084C124.702 20.7185 131.986 15.5389 131.986 15.5389Z" />
        <path
          d="M92.2558 16.1863L131.986 15.5389M92.2558 16.1863L90.2693 9.71179L128.675 9.06433L131.986 15.5389M92.2558 16.1863L92.2558 23.3084C124.702 20.7185 131.986 15.5389 131.986 15.5389"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("FLDOOR")}
        />
        <path
          d="M16.107 17.4814C21.4044 12.3017 30.6749 11.6543 39.9453 10.3594C39.9453 10.3594 32.6614 12.9492 26.0396 17.4814C22.0666 23.3085 19.4179 22.0136 19.4179 51.1491C19.4179 80.2846 26.0396 83.5218 26.0396 83.5218L39.2831 90.6438C39.2831 90.6438 33.3236 90.6438 26.0396 89.3489C18.7557 88.054 16.107 83.5218 16.107 83.5218C16.107 83.5218 10.8096 78.3422 10.8096 51.1491C10.8096 23.9559 16.107 17.4814 16.107 17.4814Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("BBUMPER")}
        />
        <path
          d="M115.718 35.6276C115.146 31.7087 113.369 24.5422 113.369 24.5422L138.658 16.9219C138.658 16.9219 147.084 27.4588 147.084 50.6496C147.084 73.8404 138.427 84.6088 138.427 84.6088L113.369 76.3706C113.369 76.3706 115.146 69.8219 115.718 65.903C116.511 60.4699 116.742 50.6496 116.742 50.6496C116.742 50.6496 116.511 41.0607 115.718 35.6276Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("FWINDSHIELD")}
        />
        <path
          d="M46.726 22.1827L71.0664 27.193L71.0664 74.4572L46.7014 78.8458C46.1898 78.938 45.6669 78.8298 45.2705 78.4936C42.9869 76.5562 35.3089 68.6901 35.3089 50.5014C35.3089 32.3746 42.9347 24.5003 45.247 22.5293C45.6557 22.181 46.2001 22.0744 46.726 22.1827Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("RWINDSHIELD")}
        />
        <path
          d="M18.7551 19.4237C19.6381 18.1286 26.0391 17.4813 26.0391 17.4813C20.7417 22.0135 20.0795 34.3152 20.0795 34.3152C20.0795 34.3152 16.1064 34.3152 15.4443 33.0203C14.7821 31.7253 17.8722 20.7188 18.7551 19.4237Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("LTAILLAMP")}
        />
        <path
          d="M19.4173 82.8743C20.3002 84.1694 26.0391 84.1692 26.0391 84.1692C20.7417 78.9895 20.0795 67.3354 20.0795 67.3354C20.0795 67.3354 16.7686 67.9828 16.1064 69.2777C15.4443 70.5726 18.5344 81.5792 19.4173 82.8743Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("RTAILLAMP")}
        />
        <path
          d="M173.043 14.8915L137.947 14.8915C137.947 14.8915 147.218 28.488 147.218 50.5015C147.218 72.515 137.285 86.759 137.285 86.759L173.043 86.759L192.246 67.3354L194.895 62.1557L194.895 38.1999L192.246 34.3151L173.043 14.8915Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("BONNET")}
        />
        <path
          d="M132.097 85.4642C131.101 86.936 127.021 94.4273 127.071 96.9892C127.093 98.1517 130.503 96.0641 132.097 94.3808C134.417 91.9316 135.444 89.7076 136.623 87.4066"
          fill="white"
        />
        <path
          d="M132.097 85.4642C131.101 86.936 127.021 94.4273 127.071 96.9892C127.093 98.1517 130.503 96.0641 132.097 94.3808C134.417 91.9316 135.444 89.7076 136.623 87.4066L132.097 85.4642Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("RMIRROR")}
        />
        <path d="M132.097 15.539C131.101 14.0671 127.021 6.78343 127.071 4.22149C127.093 3.05904 130.503 5.14662 132.097 6.82993C134.417 9.27913 136.106 11.943 137.286 14.244" />
        <path
          d="M132.097 15.539C131.101 14.0671 127.021 6.78343 127.071 4.22149C127.093 3.05904 130.503 5.14662 132.097 6.82993C134.417 9.27913 136.106 11.943 137.286 14.244L132.097 15.539Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("LMIRROR")}
        />
        <path
          d="M71.0675 74.4575L71.0675 27.1932C71.0675 27.1932 83.9942 27.0498 92.2573 26.5458C100.552 26.0398 113.447 24.6034 113.447 24.6034C113.447 24.6034 116.725 39.9125 116.758 49.8541C116.79 60.0467 113.447 76.3998 113.447 76.3998C97.024 74.4 87.7482 74.0247 71.0675 74.4575Z"
          stroke="#12191F"
          strokeWidth="1.43421"
        />
        <path
          d="M22.7282 78.342L45.2421 78.342C45.2421 78.342 35.9709 72.5149 35.3091 51.7964C34.7035 32.8359 44.0796 23.6357 45.6753 22.2108C45.7477 22.1461 45.6915 22.025 45.5945 22.0303L22.0657 23.3083C22.0657 23.3083 19.4173 29.1354 19.4173 51.7964C19.4173 74.4573 22.7282 78.342 22.7282 78.342Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("BOOTLID")}
        />
        <path
          d="M188.936 16.1863C183.671 11.0386 171.719 9.71179 171.719 9.71179L175.693 14.244C186.95 16.8338 192.247 34.3151 192.247 34.3151L194.895 38.1998L194.896 62.8031L191.585 69.2777C191.585 69.2777 187.612 84.1692 175.692 86.759L171.057 91.2912C171.057 91.2912 182.976 91.9386 192.247 81.5793C197.037 76.2265 201.458 62.1625 200.855 48.5591C200.259 35.1351 196.882 23.9558 188.936 16.1863Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("FBUMPER")}
        />
        <path
          d="M137.285 86.759L175.691 86.759L171.056 91.2912C167.745 92.5861 133.974 91.9387 133.974 91.9387L137.285 86.759Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("FRWING")}
        />
        <path
          d="M137.285 14.4059L175.691 14.4059L172.381 9.71183C169.07 8.41692 133.974 9.22625 133.974 9.22625L137.285 14.4059Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("FLWING")}
        />
        <path
          d="M71.0664 77.6947L71.0664 74.4574L46.5659 78.9896L22.7276 78.3421L26.0385 84.1692L40.6063 90.9675L51.2011 91.2912C51.2011 91.2912 56.4985 78.9896 71.0664 77.6947Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("BRWING")}
        />
        <path
          d="M71.0664 23.9558L71.0664 27.1931L45.9037 22.0135L23.0755 23.2535C22.662 23.276 22.402 22.8148 22.6353 22.4726L26.0385 17.4813L37.2955 10.3593L51.2011 10.3593C51.2011 10.3593 58.4851 22.6609 71.0664 23.9558Z"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("BLWING")}
        />
        <path
          d="M90.2713 9.71179C90.2713 9.71179 91.482 13.9107 92.2578 16.1863L92.2578 23.3084L71.7304 23.9558C63.1221 22.0134 60.4734 20.0711 60.4734 20.0711C60.4734 20.0711 55.176 16.8338 51.2029 10.3593L90.2713 9.71179Z"
          fill="white"
        />
        <path
          d="M92.2578 16.1863C91.482 13.9107 90.2713 9.71179 90.2713 9.71179L51.2029 10.3593C55.176 16.8338 60.4734 20.0711 60.4734 20.0711M92.2578 16.1863L92.2578 23.3084L71.7304 23.9558C63.1221 22.0134 60.4734 20.0711 60.4734 20.0711M92.2578 16.1863C65.1086 16.1863 60.4734 20.0711 60.4734 20.0711"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("RLDOOR")}
        />
        <path
          d="M90.2713 91.2913L92.2578 85.4642L92.2578 77.6947L71.7304 77.6947C63.1221 79.6371 60.4734 81.5794 60.4734 81.5794C60.4734 81.5794 55.176 84.8167 51.2029 91.2913L90.2713 91.2913Z"
          fill="white"
        />
        <path
          d="M92.2578 85.4642C91.482 87.7398 90.2713 91.2913 90.2713 91.2913L51.2029 91.2913C55.176 84.8167 60.4734 81.5794 60.4734 81.5794M92.2578 85.4642L92.2578 77.6947L71.7304 77.6947C63.1221 79.6371 60.4734 81.5794 60.4734 81.5794M92.2578 85.4642C65.1086 85.4642 60.4734 81.5794 60.4734 81.5794"
          stroke="#12191F"
          strokeWidth="1.43421"
          fill={getFill("RRDOOR")}
        />
        <path
          d="M194.92 63.2339C194.92 63.2339 196.515 62.2849 197.285 60.763C198.055 59.2411 198.558 43.2423 197.285 40.7714C196.011 38.3005 194.92 37.8513 194.92 37.8513L194.92 50.6549L194.92 63.2339Z"
          fill={getFill("GRILLE")}
          stroke="#12191F"
          strokeWidth="1.43421"
        />
        <path
          d="M136.623 87.4066L138.609 84.1692L113.447 76.3998C113.447 76.3998 91.5949 73.8099 71.0675 74.4575L71.0675 77.6947C71.0675 77.6947 108.149 74.4575 136.623 87.4066Z"
          fill="white"
          stroke="#12191F"
          strokeWidth="1.43421"
        />
        <path
          d="M136.623 14.2439L138.609 16.8337L113.447 24.6032C113.447 24.6032 91.5949 27.8405 71.0675 27.1929L71.0675 23.9558C71.0675 23.9558 113.447 24.6032 136.623 14.2439Z"
          fill="white"
          stroke="#12191F"
          strokeWidth="1.43421"
        />
      </g>
      <defs>
        <filter
          id="filter0_d_10921_24384"
          x="-2.94759"
          y="0"
          width="216.897"
          height="106.062"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="3.82455" />
          <feGaussianBlur stdDeviation="1.91227" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_10921_24384" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_10921_24384" result="shape" />
        </filter>
        <clipPath id="clip0_10921_24384">
          <rect width="98.4132" height="209.247" fill="white" transform="translate(0.876953 98.4132) rotate(-90)" />
        </clipPath>
      </defs>
    </svg>
  );
};
